import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ShareableLinkLockScreen from '../ShareableLinkLockScreen';
import { Button } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from '../../api';
import { ShareableLinkMainInfo } from 'models/ShareableLink';

import './ShareableLinkRequestAccess.styles.scss';

type ShareableLinkRequestAccessProps = {
  className?: string;
  shareableLinkId: string;
  mainInfo?: ShareableLinkMainInfo;
};

const ShareableLinkRequestAccess: React.FC<ShareableLinkRequestAccessProps> = (
  props,
) => {
  const {
    className,
    shareableLinkId,
    mainInfo: { email, requestedAccessAt },
  } = props;

  const [hasRequestOrReminderSent, setHasRequestOrReminderSent] =
    useState(false);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const hasAlreadyRequestedAccess = useMemo(
    () => !!requestedAccessAt,
    [requestedAccessAt],
  );

  const classes = classNames('shareable-link-request-access', className);

  const { t } = useTranslation();

  const title = useMemo(
    () =>
      t(
        hasAlreadyRequestedAccess
          ? 'stillWaitingTheAccessRights'
          : 'youAreNotGrantedPermissionAccessLink',
      ),
    [hasAlreadyRequestedAccess, t],
  );

  const description = useMemo(
    () =>
      hasAlreadyRequestedAccess
        ? t('stillWaitingTheAccessRightsDescription')
        : '',
    [hasAlreadyRequestedAccess, t],
  );

  const onSendReminder = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestAccessReminder(shareableLinkId, email);
      popSuccess(t('reminderSentSuccesfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, email, popSuccess, t, popServerError]);

  const onRequestAccess = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestAccess(shareableLinkId, email);
      popSuccess(t('yourAccessRequestubmittedSuccessfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, email, popSuccess, t, popServerError]);

  return (
    <ShareableLinkLockScreen
      title={title}
      description={description}
      className={classes}
    >
      <Button
        disabled={hasRequestOrReminderSent}
        onClick={hasAlreadyRequestedAccess ? onSendReminder : onRequestAccess}
      >
        {t(hasAlreadyRequestedAccess ? 'sendReminder' : 'requestAccess')}
      </Button>
    </ShareableLinkLockScreen>
  );
};

export default ShareableLinkRequestAccess;
